import { useState } from "react";
import { WebSignInGate } from "./WebSignInGate";
import { EXCEL_QUERY_PAGE_PATH } from "../utils/webPages";
import { getTigerResource, tigerFormat } from "../utils/tigerResource";
import { acrylicStyles, acrylicTokens } from "../theme/acrylic";
import { createDataToken, type AuthMode, type DataToken } from "../auth/webAuth";

export { DATA_TOKEN_PAGE_PATH, isDataTokenPage } from "../utils/webPages";

/**
 * Excel data access (web build): a standalone page at `/authtoken` that signs
 * the user in like the main UI does, then mints a data token for Power Query.
 * Power Query cannot use the login page, so it signs in to `/api/data/*` with
 * Basic authentication — the user's login plus this token as the password.
 * The token works on this site only and is shown once; the backend keeps no
 * copy (see `lib_tauth_rs::data_token`), and a password change revokes it.
 */
export function DataTokenPage() {
  return <WebSignInGate>{(mode) => <DataTokenPanel mode={mode} />}</WebSignInGate>;
}

function DataTokenPanel({ mode }: { mode: AuthMode }) {
  const [minted, setMinted] = useState<DataToken | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function onCreate() {
    setBusy(true);
    setError("");
    const result = await createDataToken();
    setBusy(false);
    if ("token" in result) setMinted(result);
    else setError(result.error || getTigerResource("TIGER_DATA_TOKEN_FAILED"));
  }

  async function onCopy() {
    if (!minted) return;
    try {
      await navigator.clipboard.writeText(minted.token);
      setCopied(true);
    } catch {
      // No clipboard (plain http, or permission refused): select the field so
      // Ctrl+C works instead.
      (document.getElementById("data-token-value") as HTMLInputElement | null)?.select();
    }
  }

  const title = getTigerResource("TIGER_DATA_TOKEN_TITLE");

  return (
    <div style={styles.page}>
      <main style={styles.card} aria-label={title}>
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
        </div>

        <div style={styles.body}>
          <span style={styles.message}>{getTigerResource("TIGER_DATA_TOKEN_INTRO")}</span>

          {mode === "none" && (
            <span style={styles.message}>{getTigerResource("TIGER_DATA_TOKEN_NOT_NEEDED")}</span>
          )}

          {minted && (
            <>
              <ReadOnlyField label={getTigerResource("TIGER_DATA_TOKEN_USER")} value={minted.username} />
              <label style={styles.field}>
                <span style={styles.fieldLabel}>{getTigerResource("TIGER_DATA_TOKEN_TOKEN")}</span>
                <div style={styles.copyRow}>
                  <input
                    id="data-token-value"
                    aria-label={getTigerResource("TIGER_DATA_TOKEN_TOKEN")}
                    readOnly
                    value={minted.token}
                    onFocus={(e) => e.target.select()}
                    style={{ ...styles.input, flex: 1, fontFamily: "monospace" }}
                  />
                  <button type="button" className="btn-acrylic-secondary" onClick={onCopy}>
                    {getTigerResource(copied ? "TIGER_DATA_TOKEN_COPIED" : "TIGER_DATA_TOKEN_COPY")}
                  </button>
                </div>
              </label>
              <span style={styles.note}>
                {tigerFormat(
                  getTigerResource("TIGER_DATA_TOKEN_EXPIRES"),
                  new Date(minted.expires * 1000).toLocaleDateString(),
                )}
                {minted.site && ` · ${tigerFormat(getTigerResource("TIGER_DATA_TOKEN_SITE"), minted.site)}`}
              </span>
              <span style={styles.warning}>{getTigerResource("TIGER_DATA_TOKEN_WARNING")}</span>
            </>
          )}

          <a href={EXCEL_QUERY_PAGE_PATH} style={styles.inlineLink}>
            {getTigerResource("TIGER_DATA_TOKEN_QUERY_LINK")}
          </a>

          {error && <div style={styles.error}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <a href="/" className="btn-acrylic-secondary" style={styles.link}>
            {getTigerResource("TIGER_DATA_TOKEN_BACK")}
          </a>
          {!minted && mode !== "none" && (
            <button type="button" className="btn-acrylic-primary" disabled={busy} onClick={onCreate}>
              {getTigerResource(busy ? "TIGER_DATA_TOKEN_CREATING" : "TIGER_DATA_TOKEN_CREATE")}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <input
        aria-label={label}
        readOnly
        value={value}
        onFocus={(e) => e.target.select()}
        style={styles.input}
      />
    </label>
  );
}

const styles = {
  ...acrylicStyles,
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    boxSizing: "border-box" as const,
  },
  card: {
    background: acrylicTokens.surface,
    backdropFilter: acrylicTokens.surfaceBlur,
    WebkitBackdropFilter: acrylicTokens.surfaceBlur,
    border: acrylicTokens.surfaceBorder,
    borderRadius: acrylicTokens.surfaceRadius,
    width: 560,
    maxWidth: "100%",
    boxShadow: acrylicTokens.surfaceShadow,
    overflow: "hidden" as const,
  },
  body: {
    padding: "18px 24px",
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
  },
  message: { fontSize: 14, color: "#4b5260" },
  note: { fontSize: 12, color: "#4b5260" },
  warning: { fontSize: 12, color: "#92400e" },
  field: { display: "flex", flexDirection: "column" as const, gap: 4 },
  fieldLabel: { fontSize: 12, color: "#4b5260" },
  copyRow: { display: "flex", gap: 8 },
  input: {
    padding: "7px 9px",
    borderRadius: 6,
    border: "1px solid rgba(0,0,0,0.15)",
    fontSize: 13,
  },
  link: { textDecoration: "none" },
  inlineLink: { fontSize: 13, color: "#3b5bdb" },
  error: { fontSize: 12, color: "#b91c1c" },
} as const;
